import React, { useState, useEffect } from 'react';
import { Shield, BarChart3, Brain, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const SERIF = "'PerfectNineties', Georgia, serif";
const SANS  = "'MaisonNeueBook', system-ui, sans-serif";

const Arrow = ({ color = '#2d2f24', size = 14 }) => (
  <svg width={size} height={size * 0.72} viewBox="0 0 14 10" fill="none">
    <path d="M1 5h12M8 1l5 4-5 4" stroke={color} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VaiaLogo = ({ height = 42 }) => (
  <img
    src="/vaia-icon.png"
    alt="VAIA"
    style={{ height, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
    onError={(e) => { e.target.src = '/logo.png'; }}
  />
);

const features = [
  {
    icon: Shield,
    iconBg: '#ffd87a',
    iconColor: '#1a1a1a',
    title: 'Risk Profiling',
    desc: 'Comprehensive risk assessment with personalized investment recommendations tailored to your unique financial profile and goals.',
  },
  {
    icon: BarChart3,
    iconBg: '#1a1a1a',
    iconColor: '#fff',
    title: 'Portfolio Management',
    desc: 'Advanced analytics and AI-powered insights for optimal portfolio construction, rebalancing, and performance tracking across all asset classes.',
  },
  {
    icon: Brain,
    iconBg: '#d9e2c3',
    iconColor: '#1a1a1a',
    title: 'Intelligent Reports',
    desc: 'Generate comprehensive investment proposals, IC packs, and performance reports powered by our proprietary domain-trained AI.',
  },
];

const stats = [
  { value: '99.9%', label: 'Platform Uptime' },
  { value: '500+', label: 'Portfolios Managed' },
  { value: '24/7',  label: 'AI Assistance' },
  { value: '100%',  label: 'Data Security' },
];

/* ── Login modal (inner — needs reCaptcha context) ── */
const LoginModal = ({ onClose }) => {
  const { signInWithEmail, signInWithGoogle, signInWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const captchaToken = executeRecaptcha ? await executeRecaptcha('login') : null;
      await signInWithEmail(loginData.email, loginData.password, captchaToken);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setOauthLoading('google');
    try { await signInWithGoogle(); }
    catch (error) { toast.error(error.message || 'Google login failed'); setOauthLoading(null); }
  };

  const handleMicrosoftLogin = async () => {
    setOauthLoading('microsoft');
    try { await signInWithMicrosoft(); }
    catch (error) { toast.error(error.message || 'Microsoft login failed'); setOauthLoading(null); }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
        padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        backgroundColor: '#fff', borderRadius: 20,
        width: '100%', maxWidth: 440,
        boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        border: '1px solid #e5e5e5',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '24px 28px 20px', borderBottom: '1px solid #f0f0f0',
        }}>
          <div>
            <h2 style={{ fontFamily: SERIF, fontSize: 24, color: '#1a1a1a', margin: 0, fontWeight: 400 }}>
              Welcome Back
            </h2>
            <p style={{ fontFamily: SANS, fontSize: 13, color: '#a8a8a8', margin: '4px 0 0' }}>
              Sign in to continue to your dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 8, color: '#a8a8a8',
              display: 'flex', alignItems: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 28px 28px' }}>
          {/* OAuth */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            <button
              onClick={handleGoogleLogin}
              disabled={oauthLoading === 'google' || loading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '11px 16px', border: '1px solid #e5e5e5', borderRadius: 10,
                backgroundColor: '#fff', cursor: 'pointer', fontFamily: SANS,
                fontSize: 14, color: '#1a1a1a', fontWeight: 500,
                opacity: (oauthLoading === 'google' || loading) ? 0.6 : 1,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {oauthLoading === 'google' ? 'Redirecting…' : 'Continue with Google'}
            </button>

            <button
              onClick={handleMicrosoftLogin}
              disabled={oauthLoading === 'microsoft' || loading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '11px 16px', border: '1px solid #e5e5e5', borderRadius: 10,
                backgroundColor: '#fff', cursor: 'pointer', fontFamily: SANS,
                fontSize: 14, color: '#1a1a1a', fontWeight: 500,
                opacity: (oauthLoading === 'microsoft' || loading) ? 0.6 : 1,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              {oauthLoading === 'microsoft' ? 'Redirecting…' : 'Continue with Microsoft'}
            </button>
          </div>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, backgroundColor: '#f0f0f0' }} />
            <span style={{ fontFamily: SANS, fontSize: 12, color: '#c0c0c0' }}>OR</span>
            <div style={{ flex: 1, height: 1, backgroundColor: '#f0f0f0' }} />
          </div>

          {/* Email form */}
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <Label htmlFor="email" style={{ fontFamily: SANS, fontSize: 13, color: '#555', marginBottom: 6, display: 'block' }}>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@vaia.co.in"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                required
                disabled={loading}
                className="border-gray-200 focus:border-ugmonk-yellow focus:ring-ugmonk-yellow/20"
              />
            </div>
            <div>
              <Label htmlFor="password" style={{ fontFamily: SANS, fontSize: 13, color: '#555', marginBottom: 6, display: 'block' }}>Password</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  disabled={loading}
                  style={{ paddingRight: 40 }}
                  className="border-gray-200 focus:border-ugmonk-yellow focus:ring-ugmonk-yellow/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#a8a8a8',
                    display: 'flex', padding: 0,
                  }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black font-semibold py-2.5 mt-2 rounded-xl"
              disabled={loading || oauthLoading}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          <p style={{ fontFamily: SANS, fontSize: 12, color: '#c0c0c0', textAlign: 'center', marginTop: 20 }}>
            Don't have an account?{' '}
            <a href="mailto:info@vaia.co.in" style={{ color: '#1a1a1a', fontWeight: 500, textDecoration: 'none' }}>
              Contact us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Main page ── */
const HomePageInner = () => {
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (showLogin) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [showLogin]);

  return (
    <div style={{ fontFamily: SANS, color: '#2d2f24', backgroundColor: '#fff', overflowX: 'hidden' }}>

      {/* ── Fixed Nav ── */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
        <nav style={{ backgroundColor: '#fff', borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
          <div style={{
            maxWidth: 1200, margin: '0 auto', padding: '0 40px',
            height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <VaiaLogo height={42} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              
              <button onClick={() => setShowLogin(true)} style={{
                fontFamily: SANS,
                backgroundColor: '#d4debb', border: '0.5px solid #2a2a2a',
                borderRadius: 50, padding: '7px 20px',
                fontSize: 13, color: '#2d2f24', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                Login <Arrow size={12} />
              </button>
            </div>
          </div>
        </nav>
      </div>

      {/* ── Hero ── */}
      <section style={{
        height: '100vh', position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'flex-end',
      }}>
        <img
          src="/hero-bg.jpg"
          alt=""
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center top',
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1, padding: '0 200px 100px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            backgroundColor: 'rgba(218,228,196,0.4)', border: '1px solid rgba(196,214,175,0.6)',
            borderRadius: 50, padding: '5px 14px', marginBottom: 22,
          }}>
            {/* <span style={{ fontFamily: SANS, fontSize: 11, color: '#1a1a1a', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              AI-Powered Investment Banking
            </span> */}
          </div>
          <h1 style={{
            fontFamily: SERIF, fontSize: 58, color: '#1a1a1a',
            lineHeight: 1.15, fontWeight: 400, maxWidth: 520, margin: 0,
          }}>
            Your Goals,<br />Our Mission
          </h1>
        </div>
      </section>

      {/* ── Subtitle + CTA ── */}
      <section style={{ backgroundColor: '#fff', padding: '80px 40px', textAlign: 'center' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#2a2a2a', fontWeight: 400, margin: '0 auto 20px' }}>
          Tech-Powered, Future Ready Solutions
        </h2>
        <p style={{ fontFamily: SANS, fontSize: 16, color: '#555', maxWidth: 600, margin: '0 auto 40px', lineHeight: 1.8 }}>
          Achieve smarter investment outcomes with our proprietary domain-trained LLM,
          AI-powered automation, and end-to-end investment banking platform built for
          relationship managers and product desks.
        </p>
        <button
          onClick={() => setShowLogin(true)}
          style={{
            fontFamily: SANS, backgroundColor: '#ffd87a', border: 'none',
            borderRadius: 50, padding: '12px 32px',
            fontSize: 15, color: '#1a1a1a', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500,
          }}
        >
          Get Started <Arrow size={13} />
        </button>
      </section>

      {/* ── 3 Feature Cards ── */}
      <section style={{ backgroundColor: 'rgba(234,231,217,0.8)', padding: '88px 40px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#000', fontWeight: 400, lineHeight: 1.3, margin: '0 0 16px' }}>
              Intelligent by Design
            </h2>
            <p style={{ fontFamily: SANS, fontSize: 16, color: '#666', maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>
              Our platform empowers relationship managers and clients with data-driven insights and AI-powered recommendations.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {features.map((f, i) => (
              <div key={i} style={{
                backgroundColor: '#fff', border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 16, padding: '40px 36px',
              }}>
                <div style={{
                  width: 52, height: 52, borderRadius: 12,
                  backgroundColor: f.iconBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 24,
                }}>
                  <f.icon size={24} color={f.iconColor} />
                </div>
                <h3 style={{ fontFamily: SERIF, fontSize: 22, color: '#1a1a1a', margin: '0 0 12px', fontWeight: 400 }}>
                  {f.title}
                </h3>
                <p style={{ fontFamily: SANS, fontSize: 15, color: '#666', lineHeight: 1.7, margin: 0 }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Platform capabilities grid ──
      <section style={{ backgroundColor: '#f8f6f1', padding: '80px 40px' }}>
        <div style={{ maxWidth: 1040, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#000', fontWeight: 400, lineHeight: 1.3, margin: 0 }}>
              Our Family Office Platform<br />
              Built For Complexity, Designed For Clarity
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid rgba(0,0,0,0.12)' }}>
            {[
              { n: '01', title: 'Multi-Asset Portfolio Tracking', desc: 'Monitor equities, private markets, real estate, and alternatives — all consolidated in a single real-time view.' },
              { n: '02', title: 'Multi-Currency & Multi-Entity', desc: 'Manage portfolios across 12+ currencies and multiple client entities with seamless FX and consolidated reporting.' },
              { n: '03', title: 'Institutional-Grade Reporting', desc: 'Generate IC packs, portfolio summaries, and tax reports on demand — structured for advisors and principals alike.' },
              { n: '04', title: 'Enterprise Security & Approvals', desc: 'Role-based access control, complete audit trails, and a built-in approvals workflow to keep your family office compliant.' },
            ].map((f, i) => (
              <div key={i} style={{
                padding: '44px 56px',
                borderRight:  (i === 0 || i === 2) ? '1px solid rgba(0,0,0,0.12)' : 'none',
                borderBottom: (i === 0 || i === 1) ? '1px solid rgba(0,0,0,0.12)' : 'none',
                backgroundColor: '#fff',
              }}>
                <p style={{ fontFamily: SANS, fontSize: 20, color: 'rgba(102,102,102,0.4)', margin: '0 0 10px' }}>{f.n}</p>
                <h3 style={{ fontFamily: SERIF, fontSize: 22, color: 'rgba(0,0,0,0.85)', margin: '0 0 12px', fontWeight: 400 }}>{f.title}</h3>
                <p style={{ fontFamily: SANS, fontSize: 15, color: '#666', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ── Stats ── */}
      <section style={{ backgroundColor: '#fff', padding: '72px 40px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {stats.map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '32px 16px' }}>
                <div style={{ fontFamily: SERIF, fontSize: 42, color: '#1a1a1a', marginBottom: 8 }}>{s.value}</div>
                <div style={{ fontFamily: SANS, fontSize: 12, color: '#a8a8a8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ backgroundColor: '#eae7d9', padding: '96px 40px', textAlign: 'center' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 36, color: '#1a1a1a', fontWeight: 400, margin: '0 auto 16px', maxWidth: 560 }}>
          Ready to Transform Your Investment Strategy?
        </h2>
        <p style={{ fontFamily: SANS, fontSize: 16, color: '#555', maxWidth: 520, margin: '0 auto 40px', lineHeight: 1.8 }}>
          Join us today and experience the future of personalized investment banking powered by artificial intelligence.
        </p>
        <button
          onClick={() => setShowLogin(true)}
          style={{
            fontFamily: SANS, backgroundColor: '#1a1a1a', border: 'none',
            borderRadius: 50, padding: '12px 40px',
            fontSize: 15, color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500,
          }}
        >
          Get Started Today <Arrow color="#fff" />
        </button>
      </section>

      {/* ── About VAIA ── */}
      <section style={{ backgroundColor: '#fff', padding: '88px 40px', textAlign: 'center' }}>
        <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'center' }}>
          <VaiaLogo height={46} />
        </div>
        <p style={{
          fontFamily: SERIF, fontSize: 28, color: '#1a1a1a',
          maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.55, fontWeight: 400,
        }}>
          A Sustainability And Investment Advisory Firm Building Intelligent Tools For Seamless, Insight-Led Decisions.
        </p>
        <button
          onClick={() => window.open('https://vaia.co.in', '_blank', 'noopener noreferrer')}
          style={{
            fontFamily: SANS, backgroundColor: 'transparent',
            border: '1px solid #1a1a1a', borderRadius: 50, padding: '8px 32px',
            fontSize: 14, color: '#1a1a1a', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}
        >
          Learn More About Vaia <Arrow />
        </button>
      </section>

      {/* ── Footer ── */}
      <div style={{ borderTop: '1px solid #e5e5e5', backgroundColor: '#f8f6f1', padding: '28px 40px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <VaiaLogo height={28} />
          <p style={{ fontFamily: SANS, fontSize: 13, color: '#a8a8a8', margin: 0 }}>
            &copy; 2026 Vaia Investment Advisory LLP. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Login Modal ── */}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </div>
  );
};

const HomePage = () => (
  <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
    <HomePageInner />
  </GoogleReCaptchaProvider>
);

export default HomePage;
